import { type FC, useCallback, useMemo, useState } from "react";

import type { ExecutionFlowNode } from "../../types";

import { ExecutionNodeModal } from "./ExecutionNodeModal";
import { ExecutionTicketDetails } from "./ExecutionTicketDetails";

export interface EnrichedTicketNode {
  node: ExecutionFlowNode;
  level: number;
  dependencyIds: string[];
  blockedByThisIds: string[];
}

interface ExecutionFlowGraphProps {
  nodes: ExecutionFlowNode[];
  storyKey?: string;
  branchName?: string | null;
  ticketDetailsClassName?: string;
  selectedNodeId?: string | null;
  onSelectedNodeIdChange?: (nodeId: string | null) => void;
}

function normalizeDependencies(
  nodes: ExecutionFlowNode[],
): ExecutionFlowNode[] {
  const byPlanId = new Map(nodes.map((n) => [n.plan_item_id, n]));
  const byTicket = new Map(nodes.map((n) => [n.ticket_id, n]));

  return nodes.map((node) => {
    const normalizedDeps: string[] = [];
    for (const raw of node.dependencies) {
      const dep = raw.trim();
      if (!dep) {
        continue;
      }

      const target = byPlanId.get(dep) ?? byTicket.get(dep);
      if (target) {
        normalizedDeps.push(target.plan_item_id);
      }
    }
    return { ...node, dependencies: normalizedDeps };
  });
}

function buildLevelMap(nodes: ExecutionFlowNode[]): Map<string, number> {
  const planItemMap = new Map(nodes.map((n) => [n.plan_item_id, n]));
  const levels = new Map<string, number>();

  function getLevel(
    node: ExecutionFlowNode,
    visited = new Set<string>(),
  ): number {
    const existing = levels.get(node.id);
    if (existing !== undefined) {
      return existing;
    }
    if (visited.has(node.id)) {
      return 0;
    }

    visited.add(node.id);
    if (node.dependencies.length === 0) {
      levels.set(node.id, 0);
      return 0;
    }

    const dependencyNodes = node.dependencies
      .map((depPlanId) => planItemMap.get(depPlanId))
      .filter((depNode): depNode is ExecutionFlowNode => Boolean(depNode));
    const depth =
      dependencyNodes.length === 0
        ? 0
        : Math.max(
            0,
            ...dependencyNodes.map((depNode) => getLevel(depNode, visited)),
          );

    const level = depth + 1;
    levels.set(node.id, level);
    return level;
  }

  nodes.forEach((n) => getLevel(n));
  return levels;
}

function dropStoryNode(
  nodes: ExecutionFlowNode[],
  storyKey?: string,
): ExecutionFlowNode[] {
  if (!storyKey) {
    return nodes;
  }
  const trimmedStoryKey = storyKey.trim();
  if (!trimmedStoryKey) {
    return nodes;
  }
  return nodes.filter((node) => node.plan_item_id.trim() !== trimmedStoryKey);
}

function buildEnrichedNodesByLevel(
  nodes: ExecutionFlowNode[],
  storyKey?: string,
): Map<number, EnrichedTicketNode[]> {
  const filtered = dropStoryNode(nodes, storyKey);
  const normalized = normalizeDependencies(filtered);
  const levels = buildLevelMap(normalized);

  // Build reverse dependency map: plan_item_id -> list of plan_item_ids that depend on it
  const reverseDepMap = new Map<string, string[]>();
  for (const node of normalized) {
    for (const depPlanId of node.dependencies) {
      const existing = reverseDepMap.get(depPlanId);
      if (existing) {
        existing.push(node.plan_item_id);
      } else {
        reverseDepMap.set(depPlanId, [node.plan_item_id]);
      }
    }
  }

  // Group enriched nodes by level
  const nodesByLevel = new Map<number, EnrichedTicketNode[]>();
  const sorted = [...normalized].toSorted((a, b) => {
    const levelA = levels.get(a.id) ?? 0;
    const levelB = levels.get(b.id) ?? 0;
    if (levelA !== levelB) {
      return levelA - levelB;
    }
    const idA = a.plan_item_id || a.ticket_id || a.id;
    const idB = b.plan_item_id || b.ticket_id || b.id;
    return idA.localeCompare(idB);
  });

  for (const node of sorted) {
    const level = levels.get(node.id) ?? 0;
    const enriched: EnrichedTicketNode = {
      blockedByThisIds: reverseDepMap.get(node.plan_item_id) ?? [],
      dependencyIds: node.dependencies,
      level,
      node,
    };
    const group = nodesByLevel.get(level);
    if (group) {
      group.push(enriched);
    } else {
      nodesByLevel.set(level, [enriched]);
    }
  }

  return nodesByLevel;
}

export const ExecutionFlowGraph: FC<ExecutionFlowGraphProps> = ({
  nodes,
  storyKey,
  branchName,
  ticketDetailsClassName,
  selectedNodeId,
  onSelectedNodeIdChange,
}) => {
  const [selectedNode, setSelectedNode] = useState<ExecutionFlowNode | null>(
    null,
  );
  const isControlled = selectedNodeId !== undefined;

  const resolvedSelectedNode = useMemo(() => {
    if (!selectedNodeId) {
      return null;
    }
    return nodes.find((node) => node.id === selectedNodeId) ?? null;
  }, [nodes, selectedNodeId]);

  const effectiveSelectedNode = isControlled
    ? resolvedSelectedNode
    : selectedNode;

  const mainTicket = useMemo(
    () =>
      storyKey
        ? nodes.find(
            (n) => n.plan_item_id === storyKey || n.ticket_id === storyKey,
          )
        : null,
    [nodes, storyKey],
  );

  const nodesByLevel = useMemo(
    () => buildEnrichedNodesByLevel(nodes, storyKey),
    [nodes, storyKey],
  );

  const onTicketSelect = useCallback(
    (node: ExecutionFlowNode) => {
      if (isControlled) {
        onSelectedNodeIdChange?.(node.id);
        return;
      }
      setSelectedNode(node);
    },
    [isControlled, onSelectedNodeIdChange],
  );

  return (
    <div className="space-y-4">
      <ExecutionTicketDetails
        mainTicket={mainTicket ?? null}
        nodesByLevel={nodesByLevel}
        onSelectNode={onTicketSelect}
        className={ticketDetailsClassName}
      />

      {effectiveSelectedNode && (
        <ExecutionNodeModal
          node={effectiveSelectedNode}
          branchName={branchName}
          onClose={() => {
            if (isControlled) {
              onSelectedNodeIdChange?.(null);
              return;
            }
            setSelectedNode(null);
          }}
        />
      )}
    </div>
  );
};
